import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import { lumi } from "@/lib/lumi"
import { useAuth } from "@/hooks/useAuth"
import {ArrowLeft, Send, Loader2, Paperclip, Clock, User, Save, X, Eye, EyeOff} from 'lucide-react'

const statusOptions = [
  { value: "nouveau", label: "Nouveau" },
  { value: "en_attente", label: "En attente de prise en charge" },
  { value: "en_cours", label: "En cours" },
  { value: "attente_client", label: "En attente client" },
  { value: "resolu", label: "Resolu" },
  { value: "ferme", label: "Ferme" },
  { value: "escalade", label: "Escalade" },
  { value: "planifie", label: "Planifie" },
  { value: "annule", label: "Annule" },
]

const priorityOptions = [
  { value: "faible", label: "Faible" },
  { value: "normale", label: "Normale" },
  { value: "haute", label: "Haute" },
  { value: "urgente", label: "Urgente" },
]

const statusColor: Record<string, string> = {
  nouveau: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
  en_attente: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  en_cours: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  attente_client: "text-orange-400 bg-orange-500/10 border-orange-500/20",
  resolu: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  ferme: "text-slate-400 bg-slate-500/10 border-slate-500/20",
  escalade: "text-red-400 bg-red-500/10 border-red-500/20",
  planifie: "text-purple-400 bg-purple-500/10 border-purple-500/20",
  annule: "text-slate-500 bg-slate-600/10 border-slate-600/20",
}

export default function AdminTicketDetail() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const [ticket, setTicket] = useState<any>(null)
  const [messages, setMessages] = useState<any[]>([])
  const [newMessage, setNewMessage] = useState("")
  const [isInternal, setIsInternal] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editStatus, setEditStatus] = useState("")
  const [editPriority, setEditPriority] = useState("")
  const [editAssigned, setEditAssigned] = useState("")

  useEffect(() => {
    if (id) loadData()
  }, [id])

  const loadData = async () => {
    try {
      const [ticketRes, messagesRes] = await Promise.all([
        lumi.entities.tickets.list({ filter: { _id: id }, limit: 1 }),
        lumi.entities.ticket_messages.list({
          filter: { ticketId: id },
          sort: { createdAt: 1 },
          limit: 200,
        }),
      ])
      if (ticketRes.list.length > 0) {
        const t = ticketRes.list[0]
        setTicket(t)
        setEditStatus(t.status)
        setEditPriority(t.priority)
        setEditAssigned(t.assignedTo || "")
      }
      setMessages(messagesRes.list)
    } catch (err) {
      console.error("Erreur:", err)
    } finally {
      setLoading(false)
    }
  }

  const saveTicket = async () => {
    if (!ticket?._id) return
    setSaving(true)
    try {
      const now = new Date().toISOString()
      const updates: any = {
        status: editStatus,
        priority: editPriority,
        assignedTo: editAssigned,
        updatedAt: now,
      }
      if (editStatus === "resolu" && ticket.status !== "resolu") {
        updates.resolvedAt = now
      }
      if (editStatus === "ferme" && ticket.status !== "ferme") {
        updates.closedAt = now
      }
      await lumi.entities.tickets.update(ticket._id, updates)
      await loadData()
    } catch (err) {
      console.error("Erreur sauvegarde:", err)
    } finally {
      setSaving(false)
    }
  }

  const sendMessage = async () => {
    if (!newMessage.trim() || !id) return
    setSending(true)
    try {
      let attachmentUrls: string[] = []
      if (files.length > 0) {
        const results = await lumi.tools.file.upload(files)
        attachmentUrls = results.filter((r: any) => r.fileUrl).map((r: any) => r.fileUrl)
      }

      const now = new Date().toISOString()
      await lumi.entities.ticket_messages.create({
        ticketId: id,
        message: newMessage,
        authorName: user?.userName || "Admin",
        authorRole: "admin",
        attachments: attachmentUrls,
        isInternal,
        creator: user?.userId || "",
        createdAt: now,
        updatedAt: now,
      })
      setNewMessage("")
      setFiles([])
      setIsInternal(false)
      await loadData()
    } catch (err) {
      console.error("Erreur envoi:", err)
    } finally {
      setSending(false)
    }
  }

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFiles((prev) => [...prev, ...Array.from(e.target.files!)].slice(0, 5))
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    )
  }

  if (!ticket) {
    return (
      <div className="min-h-screen pt-24 lg:pt-32 pb-20 section-padding text-center">
        <h1 className="text-xl font-bold text-white mb-4">Ticket introuvable</h1>
        <Link to="/admin/tickets" className="btn-primary text-sm">Retour</Link>
      </div>
    )
  }

  return (
    <div className="min-h-screen pt-24 lg:pt-32 pb-20">
      <div className="section-padding">
        <Link to="/admin/tickets" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" />
          Retour aux tickets
        </Link>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Ticket info */}
            <div className="card !p-6 animate-fade-in-up">
              <div className="flex flex-wrap items-start gap-2 mb-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColor[ticket.status] || ""}`}>
                  {statusOptions.find((s) => s.value === ticket.status)?.label || ticket.status}
                </span>
              </div>
              <h1 className="text-xl font-bold text-white mb-2">{ticket.subject}</h1>
              <p className="text-sm text-slate-400 leading-relaxed whitespace-pre-wrap">{ticket.description}</p>

              {ticket.attachments?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {ticket.attachments.map((url: string, i: number) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 text-xs text-cyan-400 hover:bg-slate-700">
                      <Paperclip className="w-3 h-3" /> Piece jointe {i + 1}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Messages */}
            <div className="space-y-3">
              {messages.map((msg) => (
                <div
                  key={msg._id}
                  className={`card !p-5 ${
                    msg.isInternal
                      ? "border-l-2 border-l-amber-500/50 bg-amber-500/5"
                      : msg.authorRole === "client"
                      ? "border-l-2 border-l-cyan-500/50"
                      : "border-l-2 border-l-emerald-500/50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                      msg.authorRole === "client" ? "bg-cyan-500/20 text-cyan-400" : "bg-emerald-500/20 text-emerald-400"
                    }`}>
                      {msg.authorName?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <span className="text-sm font-medium text-white">{msg.authorName}</span>
                    {msg.isInternal && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 flex items-center gap-1">
                        <EyeOff className="w-3 h-3" /> Note interne
                      </span>
                    )}
                    <span className="text-xs text-slate-500 ml-auto">
                      {new Date(msg.createdAt).toLocaleString("fr-FR")}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                  {msg.attachments?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {msg.attachments.map((url: string, i: number) => (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-800 text-xs text-cyan-400 hover:bg-slate-700">
                          <Paperclip className="w-3 h-3" /> Fichier {i + 1}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Reply */}
            <div className="card !p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-white">Repondre</h3>
                <button
                  onClick={() => setIsInternal(!isInternal)}
                  className={`text-xs px-2.5 py-1 rounded-full flex items-center gap-1 transition-colors ${
                    isInternal ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" : "bg-slate-800 text-slate-400 hover:text-white"
                  }`}
                >
                  {isInternal ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {isInternal ? "Note interne" : "Reponse client"}
                </button>
              </div>
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={isInternal ? "Note interne (invisible pour le client)..." : "Repondre au client..."}
                className="input-field min-h-[100px] resize-y mb-3"
              />
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <input type="file" multiple onChange={handleFiles} className="hidden" id="admin-msg-files" />
                  <label htmlFor="admin-msg-files" className="cursor-pointer p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-cyan-400 transition-colors">
                    <Paperclip className="w-4 h-4" />
                  </label>
                  {files.map((f, i) => (
                    <button key={i} onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                      className="text-xs text-slate-500 hover:text-red-400 flex items-center gap-1">
                      {f.name.substring(0, 15)}<X className="w-3 h-3" />
                    </button>
                  ))}
                </div>
                <button onClick={sendMessage} disabled={sending || !newMessage.trim()} className="btn-primary text-sm !py-2.5 disabled:opacity-50">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Envoyer
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Edit panel */}
            <div className="card !p-5 animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
              <h3 className="text-sm font-semibold text-white mb-4">Gestion du ticket</h3>
              <div className="space-y-4">
                <div>
                  <label className="label-field">Statut</label>
                  <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)} className="input-field text-sm">
                    {statusOptions.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-field">Priorite</label>
                  <select value={editPriority} onChange={(e) => setEditPriority(e.target.value)} className="input-field text-sm">
                    {priorityOptions.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-field">Assigne a</label>
                  <input
                    type="text"
                    value={editAssigned}
                    onChange={(e) => setEditAssigned(e.target.value)}
                    placeholder="Nom du technicien"
                    className="input-field text-sm"
                  />
                </div>
                <button onClick={saveTicket} disabled={saving} className="btn-primary w-full justify-center text-sm disabled:opacity-50">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Sauvegarder
                </button>
              </div>
            </div>

            {/* Client info */}
            <div className="card !p-5 animate-fade-in-up" style={{ animationDelay: "0.15s" }}>
              <h3 className="text-sm font-semibold text-white mb-3">Informations client</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-300">{ticket.clientName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 text-slate-500 text-center text-xs">@</span>
                  <span className="text-slate-300">{ticket.clientEmail}</span>
                </div>
                {ticket.clientPhone && (
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 text-slate-500 text-center text-xs">#</span>
                    <span className="text-slate-300">{ticket.clientPhone}</span>
                  </div>
                )}
                {ticket.company && (
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 text-slate-500 text-center text-xs">B</span>
                    <span className="text-slate-300">{ticket.company}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-500" />
                  <span className="text-slate-400 text-xs">Cree le {new Date(ticket.createdAt).toLocaleString("fr-FR")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
